/**
 * Theme system — single source of truth is core/theme/ThemeEngine.kt.
 *
 * In dev mode: presets loaded from KMP JS module (EndiagramJsApi.getPresets()).
 * In prod mode: presets loaded from server API (/themes endpoint).
 * Fallback: hardcoded dark/light presets (always available).
 *
 * The ThemeDefinition interface matches core/theme/ThemeDefinition.kt exactly.
 */

export interface ThemeDefinition {
    name: string
    seedColor?: string
    isDark?: boolean
    bg: string
    bgSubtle: string
    surface: string
    surfaceElevated: string
    border: string
    borderSubtle: string
    text: string
    textSecondary: string
    textMuted: string
    accent: string
    accentSubtle: string
    roleAction: string
    roleSource: string
    roleResult: string
    roleIntermediate: string
    roleHub: string
    roleLocus: string
    edgeInput: string
    edgeOutput: string
    edgeFlow: string
    success: string
    warning: string
    danger: string
    // Optional shape constants — null/undefined for most themes, set by Blueprint.
    cornerRadius?: number     // px; undefined → renderer default (6)
    borderWidth?: number      // px; undefined → renderer default (1)
    fontFamily?: string       // CSS font-family; undefined → renderer default (mono)
}

/**
 * Fallback presets — used when KMP/server isn't available yet.
 * These MUST match what ThemeEngine.generate() produces for the same seeds.
 * They exist only as a bootstrap — once the KMP module loads, presets are replaced.
 */
// Default seed color — used when Material is activated manually via the picker.
export const DEFAULT_MATERIAL_SEED = '#6366f1'

// Default theme on first visit — Carbon (IBM Carbon Design System). The
// bootstrap 'dark' / 'light' fallback keys snapshot Carbon's g100 + white
// themes so the pre-KMP paint matches what the curated 'carbon-dark' /
// 'carbon-light' themes produce once the KMP bundle resolves. ThemeContext
// migrates the bootstrap keys to the canonical Carbon variant keys at load.
const FALLBACK_THEMES: Record<string, ThemeDefinition> = {
    // Carbon Dark (g100) — pre-KMP snapshot. Matches themes.json Carbon dark.
    'dark': {
        name: 'Carbon',
        seedColor: '#4589ff',
        isDark: true,
        bg: '#161616', bgSubtle: '#262626', surface: '#161616', surfaceElevated: '#262626',
        border: '#6f6f6f', borderSubtle: '#393939',
        text: '#f4f4f4', textSecondary: '#c6c6c6', textMuted: '#a8a8a8',
        accent: '#4589ff', accentSubtle: '#78a9ff',
        roleAction: '#78a9ff', roleSource: '#42be65', roleResult: '#be95ff',
        roleIntermediate: '#f1c21b', roleHub: '#ff7eb6', roleLocus: '#3ddbd9',
        edgeInput: '#a8a8a8', edgeOutput: '#c6c6c6', edgeFlow: '#525252',
        success: '#42be65', warning: '#f1c21b', danger: '#ff8389',
        cornerRadius: 0, borderWidth: 1,
    },
    // Carbon Light (white theme) — pre-KMP snapshot. Matches themes.json Carbon light.
    'light': {
        name: 'Carbon',
        seedColor: '#0f62fe',
        isDark: false,
        bg: '#ffffff', bgSubtle: '#f4f4f4', surface: '#ffffff', surfaceElevated: '#f4f4f4',
        border: '#525252', borderSubtle: '#e0e0e0',
        text: '#161616', textSecondary: '#525252', textMuted: '#6f6f6f',
        accent: '#0f62fe', accentSubtle: '#78a9ff',
        roleAction: '#0f62fe', roleSource: '#198038', roleResult: '#6929c4',
        roleIntermediate: '#f1c21b', roleHub: '#9f1853', roleLocus: '#007d79',
        edgeInput: '#525252', edgeOutput: '#6f6f6f', edgeFlow: '#a8a8a8',
        success: '#198038', warning: '#f1c21b', danger: '#da1e28',
        cornerRadius: 0, borderWidth: 1,
    },
}

// Mutable themes map — keys are now per-variant: `${name-slug}-${light|dark}`.
// Starts with fallbacks, replaced by KMP presets when available.
export let THEMES: Record<string, ThemeDefinition> = { ...FALLBACK_THEMES }

/** A theme that may exist in light + dark variants. The picker groups by display name. */
export interface ThemeGroup {
    name: string                                            // display name e.g. "Blueprint"
    light?: { key: string; theme: ThemeDefinition }
    dark?: { key: string; theme: ThemeDefinition }
}

// Curated themes grouped by display name. Built from THEMES on every load.
// Material is NOT here — it's a special seed-derived themer handled separately.
export let THEME_GROUPS: ThemeGroup[] = []

// Cached engine instance — loaded once, reused forever
let _engine: any = null
async function getEngine(): Promise<any> {
    if (_engine) return _engine
    try {
        // @ts-ignore — module resolution handled by Vite
        const mod = await import('endiagram-theme')
        _engine = mod.ThemeEngineJs.getInstance()
    } catch {}
    return _engine
}

export async function loadPresetsFromEngine(): Promise<void> {
    const engine = await getEngine()
    if (!engine) return

    // Fetch themes.json (served by vite from public/ in dev, by Ktor in prod)
    // and install via the SAME KMP parser the JVM server uses. Both sides
    // converge on a single source of truth, no duplicate theme definitions.
    try {
        const response = await fetch('/themes.json')
        if (response.ok) {
            const content = await response.text()

            // Install themes into the picker's ThemeEngine instance (theme bundle).
            const pickerCount = engine.installFromJson(content)
            // ALSO install into the parser's ThemeEngine instance (api bundle).
            // The KMP JS build ships two copies of endiagram-theme.mjs — one inlined
            // into core/api's distribution. Browser modules dedupe by path, so the
            // parser sees a SEPARATE ThemeEngine singleton. Without this second
            // install, parser byName(themeName) returns null and the diagram renders
            // with default colors even though the picker shows the new theme.
            //
            // CRITICAL: installThemesFromJson is an INSTANCE method on the KMP
            // KtSingleton — must call EndiagramJsApi.getInstance() first. Calling
            // EndiagramJsApi.installThemesFromJson directly returns undefined and
            // silently no-ops, leaving the api bundle's engine with only its compiled
            // defaults (caused the "Blueprint Dark renders white nodes" bug).
            try {
                // @ts-ignore — module resolution handled by Vite
                const apiModule = await import('TheEmergentNarrative-endiagram-api')
                const apiInstance = (apiModule as any).EndiagramJsApi?.getInstance?.()
                const parserCount = apiInstance?.installThemesFromJson?.(content) ?? 0
                if (pickerCount > 0 || parserCount > 0) {
                    console.log(`[Theme] Loaded ${pickerCount} themes (parser sync: ${parserCount})`)
                }
                if (parserCount === 0) {
                    console.warn('[Theme] api-bundle install returned 0 — diagram colors may be wrong. Check that EndiagramJsApi.getInstance().installThemesFromJson is callable.')
                }
            } catch (e) {
                console.warn('[Theme] api-bundle theme sync failed:', e)
            }
        }
    } catch (e) {
        console.warn('[Theme] /themes.json fetch failed, using KMP defaults:', e)
    }

    // Now read whatever is registered (JSON-loaded or compiled defaults).
    const presets = engine.presets() as ThemeDefinition[]
    const map: Record<string, ThemeDefinition> = {}
    for (const preset of presets) {
        // Disambiguate variants — two themes with the same name but different
        // isDark must NOT collide. Key = name-slug + "-light" or "-dark".
        const slug = preset.name.toLowerCase().replace(/\s+/g, '-')
        const key = `${slug}-${preset.isDark ? 'dark' : 'light'}`
        map[key] = preset
    }
    THEMES = { ...FALLBACK_THEMES, ...map }

    // Build display-name → variants index for the picker.
    // Skip the Indigo/Paper fallbacks — they're the implicit Material default state.
    const groupMap = new Map<string, ThemeGroup>()
    for (const [key, theme] of Object.entries(THEMES)) {
        if (key === 'dark' || key === 'light') continue
        if (!groupMap.has(theme.name)) groupMap.set(theme.name, { name: theme.name })
        const g = groupMap.get(theme.name)!
        if (theme.isDark) g.dark = { key, theme }
        else g.light = { key, theme }
    }
    THEME_GROUPS = Array.from(groupMap.values())
}

export async function generateThemeFromSeed(seedColor: string, isDark: boolean = true): Promise<ThemeDefinition | null> {
    const engine = await getEngine()
    if (!engine) return null
    return engine.generate(seedColor, isDark) as ThemeDefinition
}

export async function randomTheme(isDark: boolean = true): Promise<ThemeDefinition | null> {
    const engine = await getEngine()
    if (!engine) return null
    return engine.random(isDark) as ThemeDefinition
}

export function deriveCSSVariables(theme: ThemeDefinition): Record<string, string> {
    return {
        '--bg': theme.bg,
        '--bg-subtle': theme.bgSubtle,
        '--surface': theme.surface,
        '--surface-elevated': theme.surfaceElevated,
        '--border': theme.border,
        '--border-subtle': theme.borderSubtle,
        '--text': theme.text,
        '--text-secondary': theme.textSecondary,
        '--text-muted': theme.textMuted,
        '--accent': theme.accent,
        '--accent-subtle': theme.accentSubtle,
        '--role-action': theme.roleAction,
        '--role-source': theme.roleSource,
        '--role-result': theme.roleResult,
        '--role-intermediate': theme.roleIntermediate,
        '--role-hub': theme.roleHub,
        '--role-locus': theme.roleLocus,
        '--edge-input': theme.edgeInput,
        '--edge-output': theme.edgeOutput,
        '--edge-flow': theme.edgeFlow,
        '--success': theme.success,
        '--warning': theme.warning,
        '--danger': theme.danger,
        // Shape character — Blueprint overrides these; others fall back to defaults.
        '--node-corner-radius': theme.cornerRadius != null ? `${theme.cornerRadius}px` : '6px',
        '--node-border-width': theme.borderWidth != null ? `${theme.borderWidth}px` : '1px',
        '--font-ui': theme.fontFamily ?? "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
    }
}

export const DEFAULT_THEME = 'dark'
export const THEME_NAMES = Object.keys(THEMES)
